import { useState, useEffect, useRef } from 'react';
import { useBranding } from '@/App';
import PublicNav from '@/components/PublicNav';
import { CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  healthy: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', dot: 'bg-emerald-400', icon: CheckCircle, label: 'Operational', banner: 'All Systems Operational', bannerBg: 'from-emerald-500/10 to-emerald-500/5' },
  degraded: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', dot: 'bg-amber-400', icon: AlertTriangle, label: 'Degraded', banner: 'Some Services Degraded', bannerBg: 'from-amber-500/10 to-amber-500/5' },
  down: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', dot: 'bg-red-400', icon: XCircle, label: 'Down', banner: 'Major Outage In Progress', bannerBg: 'from-red-500/10 to-red-500/5' },
};

export default function Status() {
  const branding = useBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/v1/status`);
      const json = await resp.json();
      setData(json);
      setLastUpdated(new Date());
    } catch {
      setData({ overall_status: 'degraded', services: [], incident: 'Unable to reach status endpoint.' });
      setLastUpdated(new Date());
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, 30000);
    return () => clearInterval(timerRef.current);
  }, []);

  const overall = statusConfig[data?.overall_status] || statusConfig.healthy;
  const Icon = overall.icon;

  // Group services by category
  const groups = {};
  (data?.services || []).forEach(svc => {
    const cat = svc.category || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(svc);
  });

  const timeAgo = () => {
    if (!lastUpdated) return '';
    const diff = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
    if (diff < 5) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  };

  // Auto-update the "ago" text
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="status-page" className="min-h-screen" style={{ background: '#0a0a0f' }}>
      <PublicNav />

      <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-24 pb-16">
        {/* Overall Status Banner */}
        <div data-testid="status-banner" className={`rounded-2xl border ${overall.border} bg-gradient-to-b ${overall.bannerBg} p-8 text-center mb-8`}>
          {loading ? (
            <RefreshCw className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
          ) : (
            <>
              <Icon className={`w-10 h-10 mx-auto mb-3 ${overall.color}`} />
              <h1 className={`text-2xl sm:text-3xl font-bold ${overall.color}`}>{overall.banner}</h1>
              <p className="text-sm text-slate-400 mt-2">
                Updated {timeAgo()}
                {data?.cached && <span className="ml-2 text-xs text-slate-500">(cached)</span>}
              </p>
            </>
          )}
        </div>

        {/* Incident Banner */}
        {data?.incident && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6 text-sm text-amber-400">
            {data.incident}
          </div>
        )}

        {/* Service Groups */}
        {Object.entries(groups).sort().map(([category, services]) => (
          <div key={category} className="mb-6">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-1">{category}</h2>
            <div className="space-y-1.5">
              {services.map(svc => {
                const sc = statusConfig[svc.status] || statusConfig.healthy;
                return (
                  <div key={svc.id} data-testid={`status-svc-${svc.id}`} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${sc.border} ${sc.bg} transition-all`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${sc.dot} shrink-0`} />
                    <span className="font-medium text-sm text-slate-200 flex-1">{svc.name}</span>
                    <span className={`text-xs ${sc.color}`}>{svc.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {!loading && (!data?.services || data.services.length === 0) && (
          <div className="text-center py-12 text-slate-500">
            <p>No services found.</p>
          </div>
        )}

        {/* Service Level Objectives */}
        <div id="objectives" className="mt-12 pt-8 border-t border-slate-800">
          <h2 className="text-xl font-bold text-slate-200 mb-6">Service Level Objectives</h2>
          <p className="text-sm text-slate-400 mb-6">These are the targets we aim for and how we measure them. The live indicators above show our current state in real time.</p>

          <div className="mb-8">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Targets</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="border-b border-slate-700"><th className="text-left py-2.5 px-3 text-slate-300">Metric</th><th className="text-left py-2.5 px-3 text-slate-300">Target</th><th className="text-left py-2.5 px-3 text-slate-300">What this means</th></tr></thead>
                <tbody className="text-slate-400">
                  <tr className="border-b border-slate-800/50"><td className="py-2.5 px-3">Platform availability</td><td className="py-2.5 px-3 font-mono text-slate-300">99.5% monthly</td><td className="py-2.5 px-3">API responds successfully at least 99.5% of the time over any 30-day window</td></tr>
                  <tr className="border-b border-slate-800/50"><td className="py-2.5 px-3">Health endpoint latency</td><td className="py-2.5 px-3 font-mono text-slate-300">{'<'} 100ms p95</td><td className="py-2.5 px-3">95% of /api/v1/health checks return in under 100ms</td></tr>
                  <tr className="border-b border-slate-800/50"><td className="py-2.5 px-3">Async job acknowledgment</td><td className="py-2.5 px-3 font-mono text-slate-300">{'<'} 1 second</td><td className="py-2.5 px-3">Time from POST to 202 response</td></tr>
                  <tr className="border-b border-slate-800/50"><td className="py-2.5 px-3">Webhook delivery attempt</td><td className="py-2.5 px-3 font-mono text-slate-300">{'<'} 5 seconds</td><td className="py-2.5 px-3">Time from job completion to webhook POST sent</td></tr>
                  <tr><td className="py-2.5 px-3">Incident communication</td><td className="py-2.5 px-3 font-mono text-slate-300">{'<'} 2 hours</td><td className="py-2.5 px-3">First update during business hours after a significant incident</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">How We Measure</h3>
            <div className="space-y-2 text-xs text-slate-400">
              <p><strong className="text-slate-300">Availability</strong> is calculated from rolling 30-day windows of <code className="text-slate-300 bg-slate-800 px-1.5 py-0.5 rounded">/api/v1/health</code> successful responses.</p>
              <p><strong className="text-slate-300">Latency</strong> is measured at the edge (nginx access logs), so it reflects what customers actually experience.</p>
              <p><strong className="text-slate-300">The live status indicators above are the single source of truth</strong> — we don't have a separate internal dashboard with different numbers.</p>
              <p><strong className="text-slate-300">Significant incidents</strong> trigger banner notifications on this page and (if you've registered) webhook notifications.</p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">What We Don't Promise</h3>
            <p className="text-xs text-slate-500 mb-2">To be transparent about expectations:</p>
            <div className="space-y-2 text-xs text-slate-400">
              <p><strong className="text-slate-300">Individual async job latency</strong> — depends on input size, format complexity, and current platform load. We commit to acknowledgment time, not completion time.</p>
              <p><strong className="text-slate-300">100% uptime</strong> — no honest platform commits to perfect uptime. We commit to high availability plus transparent communication when we fall short.</p>
              <p><strong className="text-slate-300">Zero incidents</strong> — incidents happen. What we commit to is timely detection, clear communication, and post-incident transparency.</p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">How You Can Monitor Compliance</h3>
            <p className="text-xs text-slate-500 mb-2">You don't have to take our word for it. You can verify yourself:</p>
            <div className="space-y-2 text-xs text-slate-400">
              <p><span className="text-slate-300 font-mono mr-1.5">1.</span> <strong className="text-slate-300">Poll <code className="bg-slate-800 px-1.5 py-0.5 rounded">/api/v1/health</code></strong> every 30-60 seconds from your monitoring system — no authentication required, 10-second Redis-cached on our side.</p>
              <p><span className="text-slate-300 font-mono mr-1.5">2.</span> <strong className="text-slate-300">Watch this status page</strong> — auto-refreshes every 30 seconds, shows per-service indicators.</p>
              <p><span className="text-slate-300 font-mono mr-1.5">3.</span> <strong className="text-slate-300">Subscribe to webhooks</strong> — register a webhook with events to get pushed notifications of job completions and failures.</p>
              <p><span className="text-slate-300 font-mono mr-1.5">4.</span> <strong className="text-slate-300">Check the response time on any API call</strong> — measure end-to-end yourself.</p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Service Credits</h3>
            <p className="text-xs text-slate-400">We don't currently offer service credits for missed objectives. This will change when we introduce enterprise contracts. For now, our commitment is operational transparency and rapid incident response, not financial remediation.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-800 text-center text-xs text-slate-500 space-y-1">
          <p>This page auto-refreshes every 30 seconds.</p>
          <p>For service updates and incident history, contact <a href="mailto:support@tensornex.cloud" className="text-slate-400 underline">support@tensornex.cloud</a></p>
          <p className="text-slate-600 mt-2">{branding.brand_name || 'Tensornex'} Platform Status</p>
        </div>
      </main>
    </div>
  );
}
